import { inject, Inject, Injectable, signal, WritableSignal } from '@angular/core';
import { ConfigInstanceToken, ConfigInterface } from '../../interfaces/config.interface';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { LoadingScreenComponent } from '../../components/loading-screen/loading-screen.component';
import { LoadingScreenModel } from '../../components/loading-screen/loading-screen.model';
import { ConfigConstants } from '../../config/config-constants';
import { CONNECTION_ERROR_CODES } from '../../enums/connection-error-codes.enum';
import { NOTIFICATION_STATUS_CODE } from '../../enums/notification-status-code.enum';
import { InitializableInterface } from '../../interfaces/initializable.interface';
import { NotificationDataInterface } from '../../interfaces/notification-data.interface';
import { NotificationInterface } from '../../interfaces/notification.interface';
import { AddressModel } from '../../models/complex/address.model';
import { TaskModel } from '../../models/complex/task.model';
import { NotificationIdentifierModel } from '../../models/low-level/notification-identifier.model';
import { NotificationModel } from '../../models/low-level/notification.model';
import { NotificationHistoryModel } from '../../models/low-level/notification-history.model';
import { StatusModel } from '../../models/low-level/status.model';
import { Equipment } from '../../types/equipment';
import { Notification } from '../../types/notification';
import { NotificationIdentifier } from '../../types/notification-identifier';
import { NotificationStatus } from '../../types/notification-status';
import { isDefined } from '../../utilities/is-defined';
import { NotificationUtilities } from '../../utilities/notification-utilities';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';
import { ConnectivityService } from '../connectivity/connectivity.service';
import { EventService } from '../event/event.service';
import { StorageNotificationStatusService } from '../notification-status/storage-notification-status.service';
import { NotificationStatusInstanceToken } from '../../interfaces/notification-status.interface';
import {
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
} from '../timesheet/timesheet-and-expenses.interface';
import { NextNotificationStatus } from './next-notification-status';
import { OdataNotificationService } from './odata-notification.service';
import { UserMasterInstanceToken, UserMasterInterface } from '../../interfaces/user-master.interface';
import { TimesheetModel } from '../../models/low-level/timesheet.model';
import { TimesheetService } from '../timesheet/timesheet.service';
import { SynchronizationInstanceToken, SynchronizationInterface } from '../../interfaces/synchronization.interface';

@Injectable({
    providedIn: 'root',
})
export class NotificationService implements InitializableInterface, NotificationInterface, NotificationDataInterface {
    private NEXT_STATUS_MATRIX: { [key: string]: NextNotificationStatus[] } = {};
    private notifications: NotificationModel[];
    public customerAddresses: AddressModel[]; // used for material-return screen
    public notificationStatusSignal!: WritableSignal<StatusModel>;

    public isInitialized = false;
    private readonly notificationStatusService = inject(NotificationStatusInstanceToken);

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly translate: TranslateService,
        private readonly eventService: EventService,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        private readonly appNotificationService: AppNotificationService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly odataNotificationService: OdataNotificationService,
        private readonly modalService: BsModalService,
        private readonly connectivityService: ConnectivityService,
        @Inject(SynchronizationInstanceToken)
        protected readonly synchronizationService: SynchronizationInterface,
        private readonly storageNotificationStatusService: StorageNotificationStatusService,
        @Inject(UserMasterInstanceToken) private readonly userMasterService: UserMasterInterface,
        private readonly timesheetService: TimesheetService,
    ) {
        this.eventService.notificationRefreshRequired$
            .pipe(
                filter((refreshRequired) => refreshRequired && this.isInitialized),
                tap(() => {
                    this.log.debug('NotificationService: notificationRefreshRequired - Refreshing notifications');
                }),
                switchMap(() =>
                    this.init().pipe(
                        tap(() => this.eventService.triggerNotificationsUpdated()),
                        catchError((error) => {
                            // making double sure that any error does not break the pipe
                            this.log.error(
                                `NotificationService: notificationRefreshRequired: error in the pipe: ${JSON.stringify(
                                    error,
                                )}`,
                            );
                            return EMPTY;
                        }),
                    ),
                ),
            )
            .subscribe();
    }

    public init(isAppStart = false): Observable<NotificationModel[]> {
        return this.odataNotificationService.getNotifications(isAppStart).pipe(
            mergeMap((notifications) => {
                return this.timesheetAndExpensesService.selectCurrentTimesheet$({ once: true }).pipe(
                    mergeMap((timesheet) => {
                        // If the current timesheet has no valid start-of-work, then check for submitted timesheet for confirmed notifications.
                        // This can only happen in P58 as in NGMAA the work schedule will always provide a valid start-of-work entry.
                        if (!timesheet.startOfWork.isValid()) {
                            return this.timesheetAndExpensesService.selectSubmittedTimesheet$().pipe(take(1));
                        }
                        return of(timesheet);
                    }),
                    mergeMap((timesheet: Readonly<TimesheetModel>) => {
                        this.notifications = notifications.sort((notification1, notification2) => {
                            const plannedStart1 = this.getNotificationStart(notification1);
                            const plannedStart2 = this.getNotificationStart(notification2);
                            if (plannedStart1.isBefore(plannedStart2)) {
                                return -1;
                            }
                            if (plannedStart2.isBefore(plannedStart1)) {
                                return 1;
                            }
                            return 0;
                        });

                        // Only display warning if there are matching notifications and this is not the first init of the service (to avoid
                        // disp laying a warning dialog during initial startup)
                        this.showNoEquipmentNotificationList(this.isInitialized);

                        // Coll ect addresses for usage in timesheet
                        // Add  addresses from ndp addressSet (each notification has same AddressSet data)
                        let addresses = this.notifications.length > 0 ? this.notifications[0].addressSet : [];

                        // Add  customer addresses for usage
                        this.customerAddresses = this.notifications.map((notification) => notification.address);
                        addresses = addresses.concat(this.customerAddresses);

                        // all  addresses from the NDP will have an address number. We use it for uniqueness.
                        addresses = _.uniqBy(addresses, 'addrNumber.value');

                        // temp orarily save existing addresses to not lose the manually created addresses within them
                        let oldAddresses = this.timesheetAndExpensesService.getAddresses();

                        // The  below logic is only if hide timesheet address is allowed
                        // Duri ng initialization, the "visible" flag is always set to true for addresses from AddressesSet.
                        // In c ases where a user has hidden an address from AddressesSet, we store those address locally.
                        // We t hen compare the NDP address with the stored address and restore the visibility flag accordingly.

                        if (this.configService.getEnableHideTimesheetAddress() && oldAddresses.length > 0) {
                            addresses.forEach((newAddress) => {
                                const oldAddress = oldAddresses.find(
                                    (address) =>
                                        newAddress.isFromAddressSet &&
                                        newAddress.addrNumber.value === address.addrNumber.value,
                                );
                                if (oldAddress) {
                                    newAddress.visible = oldAddress.visible;
                                }
                            });
                        }

                        const confirmedNotification = timesheet.notifications.some(
                            (notification) =>
                                notification.notificationId.qmNum !== '' && notification.isConfirmationSubmitted,
                        );
                        // If there are no notifictions, then keep all the last saved old addresses
                        // Otherwise just find manually created addresses and re-add them to the locally stored addresses for further use
                        if (!confirmedNotification && this.notifications.length > 0) {
                            oldAddresses = oldAddresses.filter((address) => address.addrNumber.value.length === 0);
                        }

                        // It i s possible that the saved addresses from confirmed notifications is also a part of addresses from ndp
                        // so c heck for existing address with addrNumber.value before pushing to (uniqued) addresses object
                        oldAddresses.forEach((address) => {
                            if (
                                address.addrNumber.value.length === 0 ||
                                !addresses.some((existing) => existing.addrNumber.value === address.addrNumber.value)
                            ) {
                                addresses.push(address);
                            }
                        });

                        // overwrite addresses
                        return this.timesheetAndExpensesService.saveAddresses(addresses);
                    }),
                );
            }),
            mergeMap(() => {
                // If timesheet functionality is disabled, then overlapping of time in time recording is possible so to avoid that
                // we are saving timesheet and comparing the timesheet entry EOW and in progress notification's SOW
                // If timesheet EOW date is same as in-progress SOW date then it means user is still working on same day
                // Deleting the timesheet info next day
                return this.timesheetAndExpensesService.selectCurrentTimesheet$({ once: true }).pipe(
                    mergeMap((timesheet) => {
                        const currentTimesheetEndOfWork = timesheet.getEndOfWork();

                        const isTodayTimesheetEndDate = currentTimesheetEndOfWork.isSame(new Date(), 'day');

                        if (!isTodayTimesheetEndDate) {
                            const isAnyNotificationInProgressToday = this.getAllNotificationsInProgress().some(
                                (notification) =>
                                    this.getStatus(notification).startTime.isSame(currentTimesheetEndOfWork, 'date'),
                            );
                            if (
                                !isAnyNotificationInProgressToday &&
                                (this.userMasterService.isPaceOnlineUser() ||
                                    !this.timesheetService.isTimesheetFeatureEnabled())
                            ) {
                                return this.timesheetAndExpensesService.deleteSubmittedNotificationInfoFromTimesheet(
                                    timesheet,
                                );
                            }
                        }
                        return of(undefined);
                    }),
                );
            }),
            mergeMap(() => {
                // Since a while there is the NdpHeaderSet.TaskSet correctly filled with the notifications status history.
                // After each sync, we want to use that information to re-initialize the local storage status data.
                return this.notificationStatusService.removeOldNotificationsStatus(this.notifications);
            }),
            mergeMap(() => this.updateLocalStorageStatusInformationAndStartOfWork(this.notifications, isAppStart)),
            map(() => {
                // update the local properties of notification after local storage status are updated
                this.notifications.forEach((notification) => this.setNotificationProperties(notification));
            }),
            mergeMap(() => this.handlePausedNotifications()),
            map(() => {
                // only trigger the material service init if its not called from app start
                if (!isAppStart) {
                    this.log.debug('NotificationService: init - Calling "triggerNotificationsUpdated" now.');
                    this.eventService.triggerNotificationsUpdated();
                }

                this.isInitialized = true;
                return this.notifications;
            }),
            tap(() => {
                if (this.odataNotificationService.incorrectNotificationsList.length > 0) {
                    this.log.warn(
                        `Total ${this.odataNotificationService.incorrectNotificationsList.length} notifications are not loaded.`,
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('NOTIFICATION_NOT_LOADED_ERROR_TITLE'),
                        `${<string>(
                            this.translate.instant('NOTIFICATION_NOT_LOADED_ERROR_BODY')
                        )} ${this.odataNotificationService.incorrectNotificationsList.join(',').replace(/,/g, '<br>')}`,
                    );
                }
            }),
            tap(() => {
                this.eventService.triggerUpdateNotificationCountOnSuccessfulSync();
                this.createNotificationStatusMatrix();
                if (this.configService.getDisplayServicePartQuestionnaire()) {
                    this.log.debug(
                        'NotificationService: init: getting all the spq XMLs assigned for each notification materials',
                    );

                    this.eventService.triggerSPQDocRefresh();
                }
            }),
        );
    }

    /**
     * To get first in-progress notification, find a notification with status OSR,
     * OSR has more priority than notifications with status TRAV/FCC/ROSR
     *
     * @param notificationsList
     */
    private getFirstInProgressNotification(notificationsList: NotificationModel[]): NotificationModel | undefined {
        const notificationInOSRStatus = notificationsList.find((notification) => {
            const status = this.getStatus(notification);
            return status.statusCode === NOTIFICATION_STATUS_CODE.OSR;
        });
        const firstInProgressNotification =
            notificationInOSRStatus ??
            notificationsList.find((notification) => {
                const status = this.getStatus(notification);
                return (
                    status.statusCode === NOTIFICATION_STATUS_CODE.TRAV ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.ROSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.FCC
                );
            });
        return firstInProgressNotification;
    }

    /**
     * pause or unpause notifications after ndp sync or crm is submitted
     * 1. If there are multiple in-progress notifications and all of them are paused, then
     *    get first in-progress notification and remove pause for it.
     * 2. If there are multiple in-progress notifications and multiple notifications which are not paused, then
     *    pause all other notifications except the first in-progress notification
     */
    public handlePausedNotifications(): Observable<void[] | undefined> {
        let handlePausedNotifications$: Observable<void[]> = of([]);
        if (this.configService.getAllowNotificationPause()) {
            const totalPausedNotifications = this.notifications.filter((notification) => notification.isPaused);
            const inProgressNotifications = this.getAllNotificationsInProgress();

            if (inProgressNotifications.length > 0) {
                const unpausedNotifications = inProgressNotifications.filter((notification) => !notification.isPaused);
                if (unpausedNotifications.length === 0) {
                    const notificationToBeStarted = this.getFirstInProgressNotification(inProgressNotifications);
                    if (typeof notificationToBeStarted === 'undefined') {
                        throw new Error('No notifications with in-progress status found');
                    }
                    handlePausedNotifications$ = this.savePausedIndicator([notificationToBeStarted], false);
                } else if (unpausedNotifications.length > 1) {
                    const firstInProgressNotification = this.getFirstInProgressNotification(unpausedNotifications);
                    if (typeof firstInProgressNotification !== 'undefined') {
                        const id = firstInProgressNotification.identifier;
                        const notificationsToBePaused = unpausedNotifications.filter(
                            (notif) => !notif.identifier.equalsTo(id),
                        );
                        handlePausedNotifications$ = this.savePausedIndicator(notificationsToBePaused, true);
                    }
                }
            } else if (inProgressNotifications.length === 0 && totalPausedNotifications.length > 0) {
                handlePausedNotifications$ = this.savePausedIndicator(totalPausedNotifications, false);
            }
        }
        return handlePausedNotifications$.pipe(
            catchError((error) => {
                this.log.error(
                    `NotificationService: handlePausedNotifications: error: ${JSON.stringify(
                        error,
                        Object.getOwnPropertyNames(error),
                    )}`,
                );
                return of(undefined);
            }),
        );
    }

    public showNoEquipmentNotificationList(showAppNotification: boolean): void {
        if (!showAppNotification) {
            return;
        }
        // Chec k if all notifications have an equipment attached with count received from gateway. If not, display a
        // warn ing message.
        const notificationsWithoutEquipmentFromGateway = this.notifications.filter((n) => n.equipmentsCount === 0);

        if (notificationsWithoutEquipmentFromGateway.length > 0) {
            const notificationNoEquipmentErrorBody = <string>(
                this.translate.instant('NOTIFICATION_NO_EQUIPMENT_ERROR_BODY')
            );
            this.appNotificationService.setAppNotification(
                AppNotificationType.ERROR,
                <string>this.translate.instant('NOTIFICATION_NO_EQUIPMENT_ERROR_TITLE'),
                NotificationUtilities.getNotificationsFormattedWithNewLine(
                    notificationsWithoutEquipmentFromGateway.map((notification) => notification.identifier),
                    notificationNoEquipmentErrorBody,
                ),
            );
        }

        // chec k the equipment set length for each notification and retrieve all the notifications with
        // Equi pmentsSet array not equals to equipments count
        // this means the offline store has deleted data for Equipments set
        const notificationsWithoutEquipmentFromOfflineStore = this.notifications.filter(
            (n) => n.equipmentSet.length !== n.equipmentsCount || !n.getMainEquipment(),
        );
        // all  notifications without the equipments set then try to recreate offline store
        // open a modal pop up for this so user cannot perform any other action
        if (notificationsWithoutEquipmentFromOfflineStore.length > 0) {
            this.recreateOfflineStore();
        }
    }

    private recreateOfflineStore(): void {
        const loadingScreenModel = new LoadingScreenModel(
            <string>this.translate.instant('NO_EQUIPMENT_LOADING_MESSAGE'),
        );
        const modalInstance = this.modalService.show(LoadingScreenComponent, {
            backdrop: 'static',
            class: 'modal-sm',
            initialState: {
                model: loadingScreenModel,
            },
            ariaLabelledBy: 'loading-screen-modal-title',
        });

        loadingScreenModel.loadingScreenDisplayed$
            .pipe(
                take(1),
                // make sure internet and is available, if not show no internet error and do not
                // delete offline store
                mergeMap(() => this.connectivityService.isOnlineButWithErrors$),
                mergeMap(() => this.synchronizationService.flushCompletely()),
                mergeMap(() => this.synchronizationService.receiveEverything(true)),
                catchError((error) => {
                    const isNoInternetError = error === CONNECTION_ERROR_CODES.NO_INTERNET_CONNECTION;
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.WARN,
                        <string>(
                            this.translate.instant(isNoInternetError ? 'APP_SYNC_FAILED_TITLE' : 'ERROR_REQUEST_ERROR')
                        ),
                        <string>(
                            this.translate.instant(
                                isNoInternetError ? 'APP_SYNC_FAILED_SYNCHRONIZATION' : 'ERROR_NO_INTERNET_ERROR',
                            )
                        ),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => {
                modalInstance.hide();
            });
    }

    public getNextUpcomingNotification(mainNotification: NotificationModel): NotificationModel | undefined {
        return this.notifications.find((notification) =>
            notification.plannedStart.isAfter(mainNotification.plannedStart),
        );
    }

    public getNotificationStart(notification: NotificationModel): moment.Moment {
        const duraTask = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.DURA);
        if (duraTask) {
            return duraTask.startTime;
        }
        if (notification.dispOrEtaStart) {
            return notification.dispOrEtaStart;
        }
        let statusEtaOrDisp = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.ETA);
        if (!statusEtaOrDisp) {
            statusEtaOrDisp = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.DISP);
        }
        if (statusEtaOrDisp) {
            return statusEtaOrDisp.startTime;
        } else {
            this.log.error(
                `NotificationService: Neither status ETA nor DISP was found in taskSet for ${notification.identifier.toString()}. This is an invalid situation. Fallback value plannedStart returned.`,
            );

            return notification.plannedStart;
        }
    }

    public getNotificationEnd(notification: NotificationModel): moment.Moment {
        const duraTask = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.DURA);
        if (duraTask) {
            return duraTask.endTime;
        }
        if (notification.dispOrEtaEnd) {
            return notification.dispOrEtaEnd;
        }
        let statusEtaOrDisp = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.ETA);
        if (!statusEtaOrDisp) {
            statusEtaOrDisp = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.DISP);
        }
        if (statusEtaOrDisp) {
            return statusEtaOrDisp.endTime;
        } else {
            this.log.error(
                `NotificationService: Neither status ETA nor DISP was found in taskSet for ${notification.identifier.toString()}. This is an invalid situation. Fallback value plannedEnd returned.`,
            );
            return notification.plannedEnd;
        }
    }

    /**
     * This function is currently just called from the init() function of this service.
     * It takes the values from the NdpHeaderSet.TaskSet and re-initializes the local storage status information. It
     * will overwrite existing status entries locally and add missing status, but never remove any local status.
     *
     * @param notifications
     * @param isAppStart
     */
    public updateLocalStorageStatusInformationAndStartOfWork(
        notifications: NotificationModel[],
        isAppStart: boolean,
    ): Observable<void> {
        this.log.debug(
            `NotificationService: updateLocalStorageStatusInformationAndStartOfWork: About to process ${notifications.length} notifications.`,
        );

        let earliestTRAV = moment.invalid();
        notifications.forEach((currentNotification) => {
            // Temp orary fix for https://evorait.atlassian.net/browse/SHCNGMP58-1012
            // If t he notification already has status CRM or cancellation confirmation locally stored,
            // then no need to update the local storage status information for such notifications
            if (this.getStatus(currentNotification).isConfirmedOrCancellationConfirmed()) {
                this.log.debug(
                    `NotificationService: updateLocalStorageStatusInformationAndStartOfWork: Not processing notification ${currentNotification.identifier.toString()} as its local status is ${
                        this.getStatus(currentNotification).statusCode
                    }`,
                );
                return;
            }
            this.log.debug(
                `NotificationService: updateLocalStorageStatusInformationAndStartOfWork: Processing notification ${currentNotification.identifier.toString()} with ${
                    currentNotification.taskSet.length
                } TaskSet entries.`,
            );

            const groupedTaskSet = _.groupBy(currentNotification.taskSet, (taskModel) => taskModel.taskCode.value);

            _.forEach(groupedTaskSet, (value) => {
                // We h ave an array of TaskModels here, which have all the same taskCode.
                // It m ay happen that there are more that one entries for ETA, DISP, TRAV (and more) sent from backend.
                // We n eed to sort them by TaskNumber and just use the highest one to update our local status data.
                const sortedTasks = _.sortBy(value, (taskModel) =>
                    taskModel.taskNumber.value ? parseInt(taskModel.taskNumber.value, 10) : 0,
                );

                const task = sortedTasks.pop();
                if (task) {
                    const statusModel = new StatusModel(
                        <NOTIFICATION_STATUS_CODE>task.taskCode.value,
                        task.plannedStart,
                        task.plannedEnd,
                    );
                    this.notificationStatusService.addMissingStatusToList(currentNotification.identifier, statusModel);

                    // If t he current notification's backend status code is same as locally stored status, then no need to update the status
                    if (
                        !isAppStart &&
                        currentNotification.backendStatus.statusCode === this.getStatus(currentNotification).statusCode
                    ) {
                        this.log.debug(
                            `NotificationService: updateLocalStorageStatusInformationAndStartOfWork: Not processing notification ${currentNotification.identifier.toString()} as the status ${
                                this.getStatus(currentNotification).statusCode
                            } is already updated in local`,
                        );
                        return;
                    }

                    // Addi tional check to correctly update status of cancelled notifications since CANC/AVR1/AVR2 is not present in taskSet
                    if (
                        task.taskCode.value !== currentNotification.backendStatus.statusCode &&
                        currentNotification.backendStatus.isCancelled() &&
                        !this.getCancelled(currentNotification)
                    ) {
                        this.saveCancelled(
                            currentNotification,
                            statusModel.isInProgressOrToBeSigned()
                                ? NOTIFICATION_STATUS_CODE.AVRF
                                : NOTIFICATION_STATUS_CODE.AVR1,
                        );
                    }

                    // For  NGMP58
                    // if w e re-initialize the local storage status information and go into a notification that has passed TRAV
                    // alre ady, it might be that SOW is not set which results in a validation error.
                    // we n eed to update SOW with the earliest TRAV start we get from backend
                    if (
                        statusModel.statusCode === NOTIFICATION_STATUS_CODE.TRAV &&
                        (!earliestTRAV.isValid() || statusModel.startTime.isBefore(earliestTRAV))
                    ) {
                        earliestTRAV = statusModel.startTime;
                    }
                }
            });
        });

        return this.timesheetAndExpensesService.getStartOfWork$().pipe(
            take(1),
            mergeMap((sow) => {
                let startOfWork = moment(sow);
                const today = moment();

                // if t here is a TRAV timestamp, check if there is a SoW. If yes, we might need to move it to an earlier time
                // so i t is before the earliest TRAV timestamp we have. If there is no SoW but a TRAV timestamp, we need to set
                // SoW  as well.
                if (
                    earliestTRAV.isValid() &&
                    (!startOfWork.isValid() || (startOfWork.isSame(today, 'day') && earliestTRAV.isBefore(startOfWork)))
                ) {
                    this.log.debug(
                        'NotificationService: updateLocalStorageStatusInformationAndStartOfWork: Updating SOW to ' +
                            earliestTRAV.toString(),
                    );
                    startOfWork = earliestTRAV;
                }
                return this.timesheetAndExpensesService
                    .saveStartOfWork(startOfWork)
                    .pipe(mergeMap(() => this.saveNotificationStatusModelList()));
            }),
        );
    }

    public getTodaysNotifications(): NotificationModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getTodaysNotifications: Trying to call NotificationService before initialization!`,
            );
        }

        const notifications = this.notifications.filter((notification) => this.isTodaysNotification(notification));
        return this.sortNotificationsByStatus(notifications);
    }

    public getUpcomingNotifications(): NotificationModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getUpcomingNotifications: Trying to call NotificationService before initialization!`,
            );
        }

        const notifications = this.notifications.filter((notification) => {
            const status = this.getStatus(notification);
            const plannedStart = this.getNotificationStart(notification);
            // http s://evorait.atlassian.net/browse/SHCNGMAAP2-742
            // mome nt isSame, isAfter, isBefore granularity internally compare time -
            // http s://github.com/moment/moment/issues/2427
            // star tOf('day') will not work as for UTC it will be (Japan Standard Time) - 09:00
            // whic h will be greater than moment().startOf('day') (Japan Standard Time) - 00:00
            // now  we check if the moment is UTC, if yes then we convert planned start date into local first
            // and  then using startOf('day') we ensure that time is always 00:00:00
            let plannedStartDate = plannedStart.clone();
            if (plannedStart.isUTC()) {
                plannedStartDate = plannedStartDate.local().startOf('day');
            }
            return (
                plannedStartDate.isAfter(moment().startOf('day'), 'day') &&
                !status.isConfirmedOrCancellationConfirmed() &&
                status.statusCode !== NOTIFICATION_STATUS_CODE.DISP
            );
        });
        return this.sortNotificationsByStatus(notifications);
    }

    public isTodaysNotification(notification: NotificationModel): boolean {
        const status = this.getStatus(notification);
        const plannedStart = this.getNotificationStart(notification);
        // https://evorait.atlassian.net/browse/SHCNGMAAP2-742
        // moment isSame, isAfter, isBefore granularity internally compare time -
        // https://github.com/moment/moment/issues/2427
        // startOf('day') will not work as for UTC it will be (Japan Standard Time) - 09:00
        // which will be greater than moment().startOf('day') (Japan Standard Time) - 00:00
        // now we check if the moment is UTC, if yes then we convert planned start date into local first
        // and then using startOf('day') we ensure that time is always 00:00:00
        let plannedStartDate = plannedStart.clone();
        if (plannedStart.isUTC()) {
            plannedStartDate = plannedStartDate.local().startOf('day');
        }
        return (
            plannedStartDate.isSameOrBefore(moment().startOf('day'), 'day') &&
            !status.isConfirmedOrCancellationConfirmed() &&
            status.statusCode !== NOTIFICATION_STATUS_CODE.DISP
        );
    }

    public getNewNotifications(): NotificationModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNewNotifications: Trying to call NotificationService before initialization!`,
            );
        }

        const notifications = this.notifications.filter((notification) => {
            const status = this.getStatus(notification);
            return status.statusCode === NOTIFICATION_STATUS_CODE.DISP;
        });
        return this.sortNotificationsByStartDate(notifications);
    }

    public getNotificationsHistory(): Observable<NotificationHistoryModel[]> {
        // Gets the configured number of days back in time limit
        const numberOfDays = this.configService.getNotificationsHistoryDaysLimit();
        return this.odataNotificationService.getNotificationsHistory(numberOfDays);
    }

    public getAllNotifications(): NotificationModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getAllNotifications: Trying to call NotificationService before initialization!`,
            );
        }

        return this.notifications;
    }

    public getNotificationByIdentifier(
        notificationIdentifier: NotificationIdentifierModel,
    ): NotificationModel | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNotificationByIdentifier: Trying to call NotificationService before initialization!`,
            );
        }

        return this.notifications.find((notification) =>
            NotificationIdentifierModel.compare(notification.identifier, notificationIdentifier),
        );
    }

    public getNotificationStateByIdentifier(notificationIdentifier: NotificationIdentifier): Notification | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNotificationStateByIdentifier: Trying to call NotificationService before initialization!`,
            );
        }

        const identifierModelFromState = new NotificationIdentifierModel(
            notificationIdentifier.frontId,
            notificationIdentifier.qmNum,
            notificationIdentifier.maNum,
        );
        const foundNotification = this.notifications.find((notification) =>
            NotificationIdentifierModel.compare(notification.identifier, identifierModelFromState),
        );

        return foundNotification ? foundNotification.getNotificationState() : undefined;
    }

    public getMainEquipmentFromNotification(notification: Notification): Equipment | undefined {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getMainEquipmentFromNotification: Trying to call NotificationService before initialization!`,
            );
        }

        return notification.equipmentSet.find(
            (equipment) => equipment.mainEquipmentIndicator === ConfigConstants.sapTrue,
        );
    }

    public getNotificationsEquipments(): Equipment[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNotificationByEquipment: Trying to call NotificationService before initialization!`,
            );
        }

        return this.notifications
            .map((n) => this.getMainEquipmentFromNotification(n.getNotificationState()))
            .filter(isDefined);
    }

    public getNotificationByEquipment(equipmentId: string): NotificationModel[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNotificationByEquipment: Trying to call NotificationService before initialization!`,
            );
        }

        return this.notifications.filter((n) => {
            const mainEquipment = n.getMainEquipment();
            return mainEquipment && mainEquipment.equnr.value === equipmentId;
        });
    }

    public getNotificationStateByEquipment(equipmentId: string): Notification[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getNotificationStateByEquipment: Trying to call NotificationService before initialization!`,
            );
        }

        return this.notifications
            .filter((n) => {
                const mainEquipment = n.getMainEquipment();
                return mainEquipment && mainEquipment.equnr.value === equipmentId;
            })
            .map((n) => n.getNotificationState());
    }

    // Retu rns unique SPQ ids which are assigned to each/some materials of different notifications
    public getAllSPQDocIDs(): string[] {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: getAllSPQDocIDs: Trying to call NotificationService before initialization!`,
            );
        }

        this.log.debug(
            'NotificationService: getAllSPQDocIDs: getting all the unique spq doc ids from each notification materials',
        );
        // usin g the spread operator with Set to ensure only unique SPQ doc ids are returned from the notification's part set
        return [
            ...new Set(
                this.notifications
                    .filter((notification) => notification.updatedStatus.statusCode !== NOTIFICATION_STATUS_CODE.CRM)
                    .map((notification) =>
                        notification.partSet
                            .map((part) => part.spqDocID)
                            .filter((docID) => isDefined(docID) && docID !== ''),
                    )
                    .reduce((allSpqIds, spqIds) => allSpqIds.concat(spqIds), []),
            ),
        ];
    }

    /// //// /////////////////////////////////////////////////////////////////////////////////////////////////////////////
    // STAT US HANDLING                                                                                                //
    /// //// /////////////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * It calls the functions in this class which calculate the start and end date information and
     * checks if the end date is after the start date. If so, the notification lasts longer than one day.
     *
     * @param notification
     * @returns {boolean} - true if the notification lasts longer than one day, false otherwise
     */
    public isMultiDayNotification(notification: NotificationModel): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: isMultiDayNotification: to call NotificationService before initialization!`,
            );
        }

        const plannedStart = this.getNotificationStart(notification);
        const plannedEnd = this.getNotificationEnd(notification);
        return plannedEnd.clone().startOf('day').diff(plannedStart.clone().startOf('day')) > 0;
    }

    public isNotificationInProgress(notificationIdentifier: NotificationIdentifierModel): boolean {
        if (!this.isInitialized) {
            throw new Error(
                `NotificationService: isNotificationInProgress: Trying to call NotificationService before initialization!`,
            );
        }

        const notification = this.getNotificationByIdentifier(notificationIdentifier);
        if (!notification) {
            return false;
        }
        return this.getStatus(notification).isInProgress();
    }

    public initializeNotificationStatus(notification: NotificationModel): void {
        const initialNotificationStatus = this.getStatus(notification);
        this.notificationStatusSignal = signal<StatusModel>(initialNotificationStatus);
    }

    public saveStatus(notification: NotificationModel, status: StatusModel): Observable<void> {
        // For  ETA status save it same as backend would create an ETA task
        // (eta end datetime = start Datetime for ETA sent by user with TravStatusUpdate service)
        if (status.statusCode === NOTIFICATION_STATUS_CODE.ETA) {
            status.endTime = status.startTime.clone();
        }

        this.log.debug(`${this.constructor.name}: saveStatus`, {
            notificationId: notification.identifier.toJSON(),
            status: status.toString(),
        });
        return this.notificationStatusService.saveNotificationStatus(notification.identifier, status).pipe(
            tap((isNewStatus) => {
                this.log.debug(`${this.constructor.name}: saveStatus: result`, {
                    notificationId: notification.identifier.toJSON(),
                    status: status.toString(),
                    isNewStatus,
                });
                if (!isNewStatus) {
                    return;
                }
                // once the newly selected status gets saved in the file storage then will update the "updatedStatus" value.
                // If sync happens in between saving status to file storage
                // then file storage might not return the latest selected status correctly
                // So, in that case this code will override the "updatedStatus" value with the latest selected status.
                notification.updatedStatus = status;
                this.notificationStatusSignal?.set(status);
            }),
            map(() => undefined),
        );
    }

    public getStatus(notification: NotificationModel): StatusModel {
        if (notification.updatedStatus) {
            return notification.updatedStatus;
        } else {
            const statusFromLocalStorage = this.notificationStatusService.getLatestNotificationStatusForId(
                notification.identifier,
            );
            if (statusFromLocalStorage) {
                return statusFromLocalStorage;
            } else {
                return notification.backendStatus;
            }
        }
    }

    public getStatusStateByIdentifier(notificationIdentifier: NotificationIdentifier): NotificationStatus {
        const notification = this.getNotificationStateByIdentifier(notificationIdentifier);

        if (typeof notification === 'undefined') {
            throw new Error(
                `NotificationService: getStatusStateByIdentifier: Could not find notification for identifier '${JSON.stringify(
                    notificationIdentifier,
                )}'`,
            );
        }

        if (notification.updatedStatus) {
            return notification.updatedStatus;
        } else {
            const statusFromLocalStorage = this.notificationStatusService.getLatestNotificationStatusForId(
                new NotificationIdentifierModel(
                    notification.identifier.frontId,
                    notification.identifier.qmNum,
                    notification.identifier.maNum,
                ),
            );
            if (statusFromLocalStorage) {
                return statusFromLocalStorage.getStatusState();
            } else {
                return notification.backendStatus;
            }
        }
    }

    public getNotificationTaskByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): TaskModel | undefined {
        return notification.taskSet?.find((task) => task.taskCode.value === statusCode);
    }

    public getNotificationStatusByStatusType(
        notification: NotificationModel,
        statusCode: NOTIFICATION_STATUS_CODE,
    ): StatusModel | undefined {
        let statusModel: StatusModel | undefined;
        if (notification.taskSet && notification.taskSet.length > 0) {
            let desiredTasks = notification.taskSet.filter((taskModel) => taskModel.taskCode.value === statusCode);
            if (desiredTasks && desiredTasks.length > 1) {
                desiredTasks = _.sortBy(desiredTasks, (taskModel: TaskModel) =>
                    taskModel.taskNumber.value ? parseInt(taskModel.taskNumber.value, 10) : 0,
                );
            }
            const task = _.last(desiredTasks);
            if (task) {
                statusModel = new StatusModel(
                    <NOTIFICATION_STATUS_CODE>task.taskCode.value,
                    task.plannedStart,
                    task.plannedEnd,
                );
            }
        }
        if (typeof statusModel === 'undefined') {
            statusModel = this.notificationStatusService.getNotificationStatusByStatusType(
                notification.identifier,
                statusCode,
            );
        }
        // ETA  task in backend is created with (eta start Datetime = Disp Start Datetime) and
        // (eta end datetime = start Datetime for ETA sent by user with TravStatusUpdate service)
        // ETA  task timestamps has to be calculated again to be displayed correctly at UI
        if (statusModel && statusCode === NOTIFICATION_STATUS_CODE.ETA) {
            let statusDISP = this.getNotificationStatusByStatusType(notification, NOTIFICATION_STATUS_CODE.DISP);
            if (!statusDISP) {
                // Crea te fallback DISP status with zero duration
                statusDISP = new StatusModel(
                    NOTIFICATION_STATUS_CODE.DISP,
                    statusModel.startTime.clone(),
                    statusModel.startTime.clone(),
                );
                this.log.warn(`Notification ${notification.identifier.toString()} has status ETA, but not DISP!`);
            }
            const dispDurationInMinutes = moment.duration(statusDISP.endTime.diff(statusDISP.startTime)).asMinutes();
            const etaStartTime = statusModel.endTime.clone();
            const etaEndTime = statusModel.endTime.clone().add(dispDurationInMinutes, 'minutes');

            return new StatusModel(NOTIFICATION_STATUS_CODE.ETA, etaStartTime, etaEndTime);
        }
        return statusModel;
    }

    public saveEscalated(notification: NotificationModel, escalated: boolean): Observable<void> {
        const currentStatus = this.getStatus(notification);

        // only save escalation if the notification can actually be escalated
        if (escalated && !currentStatus.canEscalate()) {
            this.log.warn(
                `NotificationService: Cannot set 'escalated' on status '${
                    currentStatus.statusCode
                }' for notification ${notification.identifier.toString()}`,
            );

            escalated = false;
        }
        notification.isEscalated = escalated;

        return this.notificationStatusService.saveEscalated(notification.identifier, escalated);
    }

    public getEscalated(notification: NotificationModel): boolean {
        if (typeof notification.isEscalated !== 'undefined') {
            return notification.isEscalated;
        }
        return this.notificationStatusService.getEscalated(notification.identifier);
    }

    public saveCancelled(notification: NotificationModel, statusCode: NOTIFICATION_STATUS_CODE): void {
        notification.isCancelled = statusCode;

        this.notificationStatusService.saveCancelled(notification.identifier, statusCode);
    }

    public saveNotificationStatusModelList(): Observable<void> {
        return this.notificationStatusService.saveNotificationStatusList();
    }

    public getCancelled(notification: NotificationModel): string {
        if (typeof notification.isCancelled !== 'undefined') {
            return notification.isCancelled;
        }
        return this.notificationStatusService.getCancelled(notification.identifier);
    }

    public saveUnreadIndicator(notification: NotificationModel, unread: boolean): Observable<void> {
        if (this.getStatus(notification).statusCode === NOTIFICATION_STATUS_CODE.DISP && notification.isUnread) {
            notification.isUnread = unread;

            return this.notificationStatusService.saveUnreadIndicator(notification.identifier, unread);
        }
        return of(undefined);
    }

    public getUnreadIndicator(notification: NotificationModel): boolean {
        // Show unread indicator only for DISP notifications
        if (this.getStatus(notification).statusCode !== NOTIFICATION_STATUS_CODE.DISP) {
            return false;
        }
        if (typeof notification.isUnread !== 'undefined') {
            return notification.isUnread;
        }
        return this.notificationStatusService.getUnreadIndicator(notification.identifier) || false;
    }

    public savePausedIndicator(notificationList: NotificationModel[], paused: boolean): Observable<void[]> {
        const savePausedIndicator$: Array<Observable<void>> = [];
        notificationList.forEach((notification: NotificationModel) => {
            notification.isPaused = this.getStatus(notification).isInProgress() && paused;
            savePausedIndicator$.push(
                this.notificationStatusService.savePausedIndicator(notification.identifier, paused),
            );
        });
        return forkJoin([...savePausedIndicator$]);
    }

    public getPausedIndicator(notification: NotificationModel): boolean {
        if (!this.getStatus(notification).isInProgress()) {
            return false;
        }
        return this.notificationStatusService.getPausedIndicator(notification.identifier) || false;
    }

    public getAvailableStatus(notification: NotificationModel): NOTIFICATION_STATUS_CODE[] {
        const currentStatus = this.getStatus(notification);
        const availableStatus = this.NEXT_STATUS_MATRIX[currentStatus.statusCode];

        if (!availableStatus) {
            this.log.warn(
                'NotificationService: There are no next possible statusCode for statusCode:',
                currentStatus.statusCode,
            );
            return [];
        }

        // first enable every status by default (necessary if 'enabled' of a status was set to 'false' previously
        // since we are re-using the objects in the status matrix over all notifications)
        availableStatus.forEach((status) => {
            status.enabled = true;
        });

        // Special treatment for ToBeSigned, OSR and notifications with OSR completed in the past already
        let scrmEnabled = false;
        if (this.shortConfirmationConditionsFulfilled(notification)) {
            scrmEnabled =
                currentStatus.isToBeSigned() ||
                currentStatus.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
                currentStatus.isRemoteWork();
        }

        const scrmStatus = availableStatus.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.SCRM);
        if (scrmStatus) {
            scrmStatus.enabled = scrmEnabled;
        }

        // check if another notification is on progress and disable TRAV/OSR if yes
        const notificationInProgress = this.getNotificationInProgress();
        if (
            notificationInProgress &&
            !NotificationIdentifierModel.compare(notificationInProgress.identifier, notification.identifier)
        ) {
            const allowMultipleInProgressNotifications = this.configService.getAllowNotificationPause();
            const travStatus = availableStatus.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.TRAV);
            if (travStatus) {
                travStatus.enabled = allowMultipleInProgressNotifications;
            }
            const osrStatus = availableStatus.find((status) => status.statusCode === NOTIFICATION_STATUS_CODE.OSR);
            if (osrStatus) {
                osrStatus.enabled = allowMultipleInProgressNotifications;
            }
        }

        // build array of StatusCodes from enabled NextNotificationStatuses
        return availableStatus
            .filter((as: NextNotificationStatus) => as.enabled)
            .map((as: NextNotificationStatus) => as.statusCode);
    }

    private shortConfirmationConditionsFulfilled(notification: NotificationModel): boolean {
        // check if "today" is before the end date
        return (
            this.isMultiDayNotification(notification) &&
            moment().isBefore(this.getNotificationEnd(notification), 'days')
        );
    }

    public getNotificationInProgress(): NotificationModel | undefined {
        return this.notifications.find((notificationModel) => this.getStatus(notificationModel).isInProgress());
    }

    // It i s possible to have more than one notification in-progress
    // exam ple: user saved a notification as TOBS, started OSR for another and then reinstalled the app,
    // then both the notifications will have OSR status coming with NDP
    public getAllNotificationsInProgress(): NotificationModel[] {
        return this.notifications.filter((notificationModel) => this.getStatus(notificationModel).isInProgress());
    }

    public getAllNotificationsInEtaStatus(): NotificationModel[] {
        return this.notifications.filter(
            (notificationModel) => this.getStatus(notificationModel).statusCode === NOTIFICATION_STATUS_CODE.ETA,
        );
    }

    public getNotificationToBeSigned(): NotificationModel | undefined {
        return this.notifications.find((notificationModel) => this.getStatus(notificationModel).isToBeSigned());
    }

    public getAllNotificationsToBeSigned(): NotificationModel[] {
        return this.notifications.filter((notificationModel) => this.getStatus(notificationModel).isToBeSigned());
    }

    public async isNotificationInProgressAndCancelledAsync(notification: NotificationModel): Promise<boolean> {
        const isConfirmationStarted = await this.storageNotificationStatusService.getConfirmationStartedAsync(
            notification.identifier,
        );

        return (
            this.isNotificationCancelled(notification) &&
            (this.getStatus(notification).isInProgressOrToBeSigned() || isConfirmationStarted)
        );
    }

    public isNotificationCancelled(notification: NotificationModel): boolean {
        return this.getCancelled(notification).length > 0;
    }

    private sortNotificationsByStatus(notifications: NotificationModel[]): NotificationModel[] {
        return notifications.sort((notification1, notification2) => {
            // for  NGMAA, notifications with TOBS status also should be shown on top
            const status1 = this.getStatus(notification1);
            const status2 = this.getStatus(notification2);
            if (status1.isInProgress()) {
                return -1;
            }
            if (status2.isInProgress()) {
                return 1;
            }
            return 0;
        });
    }

    public sortNotificationsByStartDate(notifications: NotificationModel[]): NotificationModel[] {
        const sortedNotifications = notifications.sort((notification1, notification2) => {
            const plannedStart1 = this.getNotificationStart(notification1);
            const plannedStart2 = this.getNotificationStart(notification2);
            if (plannedStart1.isBefore(plannedStart2)) {
                return -1;
            }
            if (plannedStart2.isBefore(plannedStart1)) {
                return 1;
            }
            return 0;
        });
        return sortedNotifications;
    }

    public saveCancellationConfirmationForInprogress(identifier: NotificationIdentifierModel): Observable<unknown> {
        return this.notificationStatusService.saveCancellationConfirmationForInprogress(identifier);
    }

    public removeCancellationConfirmationForInprogress(): void {
        this.notificationStatusService.removeCancellationConfirmationForInprogress();
    }

    public async getCancellationConfirmationForInprogress(id: NotificationIdentifierModel): Promise<boolean> {
        return this.notificationStatusService.getCancellationConfirmationForInprogress(id);
    }

    /**
     * This function checks if there is any notification other than the current notification in status DISP
     *
     * @param currentNotification
     */
    public isAnyOtherNotificationInStatusDisp(currentNotification: NotificationModel): boolean {
        const dispNotification = this.notifications.find((notification: NotificationModel) => {
            return (
                this.getStatus(notification).statusCode === NOTIFICATION_STATUS_CODE.DISP &&
                !this.getCancelled(notification) &&
                !NotificationIdentifierModel.compare(notification.identifier, currentNotification.identifier)
            );
        });
        return typeof dispNotification !== 'undefined';
    }

    /**
     * This function sets the in-memory properties for the Notification and is called whenever notification-list is refreshed.
     *
     * @param notification
     */
    public setNotificationProperties(notification: NotificationModel): void {
        notification.dispOrEtaStart = this.getNotificationStart(notification);
        notification.dispOrEtaEnd = this.getNotificationEnd(notification);
        notification.updatedStatus = this.getStatus(notification);
        notification.isEscalated = this.getEscalated(notification);
        notification.isCancelled = this.getCancelled(notification);
        notification.isUnread = this.getUnreadIndicator(notification);
        notification.isPaused = this.getPausedIndicator(notification);
        this.log.debug(
            `NotificationService: setNotificationProperties for notification ${notification.identifier.toString()}
                - Setting "dispOrEtaStart" to "${notification.dispOrEtaStart.toString()}"
                - Setting "dispOrEtaEnd" to "${notification.dispOrEtaEnd.toString()}"
                - Setting "updatedStatus" to "${notification.updatedStatus.toString()}"`,
        );
    }

    public isNotificationRemoteWork(notification: NotificationModel): boolean {
        return this.configService.getEnableRemoteWork() && this.getStatus(notification).isRemoteWork();
    }

    public synchronizeNotificationsCrossDevice(): Observable<NotificationModel[]> {
        return this.synchronizationService.sendOfflineDataAndReceiveNotificationsAndBootStock().pipe(
            mergeMap(() => this.notificationStatusService.init()),
            mergeMap(() => this.init()),
        );
    }

    public displayInstallationPaperwork(notificationId: NotificationIdentifierModel): boolean {
        const notification = this.getNotificationByIdentifier(notificationId);
        if (!notification) {
            return false;
        }
        return (
            this.configService.getEnableInstallationPaperwork() &&
            notification.notificationType === ConfigConstants.NotificationTypes.Installation &&
            Object.keys(notification.installationPaperworkQuestions).length > 0 &&
            Object.values(notification.installationPaperworkQuestions).some((questions) => questions.Required)
        );
    }

    private createNotificationStatusMatrix(): void {
        const nextStatus = (code: NOTIFICATION_STATUS_CODE): NextNotificationStatus => new NextNotificationStatus(code);

        const enableSAF = this.configService.getEnableSafetyAssessmentForm();

        const baseMatrix = this.getSharedStatuses(nextStatus);
        const conditionalMatrix = this.getConditionalStatuses(enableSAF, nextStatus);

        this.NEXT_STATUS_MATRIX = {
            ...baseMatrix,
            ...conditionalMatrix,
        };
    }

    private getSharedStatuses(
        nextStatus: (code: NOTIFICATION_STATUS_CODE) => NextNotificationStatus,
    ): Partial<Record<NOTIFICATION_STATUS_CODE, NextNotificationStatus[]>> {
        return {
            ROSR: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            FCC: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            OSR: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            TOBS: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            RTOBS: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            FTOBS: [nextStatus(NOTIFICATION_STATUS_CODE.CRM), nextStatus(NOTIFICATION_STATUS_CODE.SCRM)],
            CRM: [],
        };
    }

    private getConditionalStatuses(
        enableSAF: boolean,
        nextStatus: (code: NOTIFICATION_STATUS_CODE) => NextNotificationStatus,
    ): Partial<Record<NOTIFICATION_STATUS_CODE, NextNotificationStatus[]>> {
        const statuses: Partial<Record<NOTIFICATION_STATUS_CODE, NextNotificationStatus[]>> = {
            DISP: enableSAF
                ? [
                      nextStatus(NOTIFICATION_STATUS_CODE.ETA),
                      nextStatus(NOTIFICATION_STATUS_CODE.FCC),
                      nextStatus(NOTIFICATION_STATUS_CODE.TRAV),
                      nextStatus(NOTIFICATION_STATUS_CODE.ROSR),
                  ]
                : [
                      nextStatus(NOTIFICATION_STATUS_CODE.ETA),
                      nextStatus(NOTIFICATION_STATUS_CODE.FCC),
                      nextStatus(NOTIFICATION_STATUS_CODE.TRAV),
                      nextStatus(NOTIFICATION_STATUS_CODE.OSR),
                      nextStatus(NOTIFICATION_STATUS_CODE.ROSR),
                  ],

            ETA: enableSAF
                ? [
                      nextStatus(NOTIFICATION_STATUS_CODE.REMT),
                      nextStatus(NOTIFICATION_STATUS_CODE.ROSR),
                      nextStatus(NOTIFICATION_STATUS_CODE.FCC),
                      nextStatus(NOTIFICATION_STATUS_CODE.TRAV),
                  ]
                : [
                      nextStatus(NOTIFICATION_STATUS_CODE.REMT),
                      nextStatus(NOTIFICATION_STATUS_CODE.ROSR),
                      nextStatus(NOTIFICATION_STATUS_CODE.FCC),
                      nextStatus(NOTIFICATION_STATUS_CODE.TRAV),
                      nextStatus(NOTIFICATION_STATUS_CODE.OSR),
                  ],

            TRAV: enableSAF
                ? [nextStatus(NOTIFICATION_STATUS_CODE.ATS), nextStatus(NOTIFICATION_STATUS_CODE.OSR)]
                : [nextStatus(NOTIFICATION_STATUS_CODE.OSR)],
        };

        const etaNextStatuses = statuses[NOTIFICATION_STATUS_CODE.ETA] || [];
        statuses[NOTIFICATION_STATUS_CODE.DURA] = [...etaNextStatuses];

        if (enableSAF) {
            statuses[NOTIFICATION_STATUS_CODE.ATS] = [nextStatus(NOTIFICATION_STATUS_CODE.OSR)];
        }

        return statuses;
    }

    public getNotificationsInOSROrHigher(): Notification[] {
        return this.notifications
            .filter((notification) => {
                const status = this.getStatus(notification);
                return (
                    status.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.SOSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.TOBS ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.ROSR ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.FCC ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.RTOBS ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.FTOBS ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.CRM ||
                    status.statusCode === NOTIFICATION_STATUS_CODE.SCRM
                );
            })
            .map((notification) => notification.getNotificationState());
    }
}
